import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ChevronDown, Copy, FileDown, GripVertical, Home, Info, MoreHorizontal, Plus, RefreshCw, Settings2, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  WIDGET_INFO,
  orderForPhone,
  widgetFilterChips,
  widgetTitle,
  effectiveFilter,
  type Dashboard,
  type DashboardDataResponse,
  type DashboardListItem,
  type DashboardPrefs,
  type DashboardTemplate,
  type DashFilter,
  type DashSet,
  type Widget,
  type WidgetResult,
  type WidgetSize,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { queryClient } from '../../lib/board';
import { deviceClass, recentBoardIds } from '../../lib/boardNav';
import { realtime } from '../../lib/realtime';
import { useNav } from '../../lib/structure';
import { toast } from '../../lib/toast';
import { inputCls } from '../AdminPage';
import { Button, DropdownContent, DropdownItem, DropdownLabel, DropdownMenu, DropdownSeparator, DropdownTrigger, IconButton, Sheet, cx } from '../ui/ui';
import { navigate } from '../../lib/router';
import { AddWidgetSheet, DashFilterSheet, NewDashboardSheet, SIZE_TEXT, WidgetSettings } from './editor';
import { ItemsSheet, type ItemsRequest } from './ItemsSheet';
import { SCROLL_KEY, WidgetBody, widgetNotes } from './widgets';
import { DashboardReportDialog } from '../reports/DashboardReportDialog';
import { usePdfAvailable } from '../reports/ReportJob';

type ListRes = { dashboards: DashboardListItem[]; prefs: DashboardPrefs; adminTeams: { id: string; name: string }[] };
/** Rahmen für Editor: Einsatzort und Team (Boardauswahl je nach Ort) */
export type DashCtx = { scope: Dashboard['scope']; teamId: string | null };
type Draft = { title: string; filter: DashFilter; widgets: Widget[] };

const SPAN: Record<WidgetSize, string> = { 12: 'md:col-span-2 lg:col-span-12', 6: 'lg:col-span-6', 4: 'lg:col-span-4' };

export function useDashboards(enabled = true) {
  return useQuery({ queryKey: ['dashboards'], queryFn: () => api.get<ListRes>('/api/dashboards'), enabled, staleTime: 30_000 });
}

/** Persönliche Übersicht (Dashboard-Konzept, Paket B): ruhige Arbeitsübersicht, Anpassen erst nach Klick */
export function DashboardView({ selectedId, onSelect }: { selectedId: string | null; onSelect: (id: string) => void }) {
  const list = useDashboards();
  const created = useRef(false);
  const [newOpen, setNewOpen] = useState(false);

  // Erste Übersicht: aus der Vorlage „Mein Arbeitstag“ – sofort nutzbar (A01)
  useEffect(() => {
    if (list.data && !list.data.dashboards.some((d) => d.scope === 'personal') && !created.current) {
      created.current = true;
      void api.post<Dashboard>('/api/dashboards', { template: 'arbeitstag' }).then((d) => {
        queryClient.setQueryData(['dashboard', d.id], d);
        void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      });
    }
  }, [list.data]);

  const all = list.data?.dashboards ?? [];
  const prefs = list.data?.prefs;
  const id = (selectedId && all.some((d) => d.id === selectedId) ? selectedId : null) ?? (prefs?.lastDashboardId && all.some((d) => d.id === prefs.lastDashboardId) ? prefs.lastDashboardId : null) ?? all[0]?.id ?? null;

  useEffect(() => {
    if (id && prefs && prefs.lastDashboardId !== id)
      void api
        .put<DashboardPrefs>('/api/me/dashboard-prefs', { lastDashboardId: id })
        .then((p) => queryClient.setQueryData<ListRes>(['dashboards'], (old) => (old ? { ...old, prefs: p } : old)))
        .catch(() => undefined);
  }, [id, prefs]);

  const create = async (template: DashboardTemplate, title: string, teamId: string | null) => {
    const d = await api.post<Dashboard>('/api/dashboards', { template, title, ...(teamId ? { teamId } : {}) });
    queryClient.setQueryData(['dashboard', d.id], d);
    await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    onSelect(d.id);
  };

  if (list.isError) return <p role="alert" className="text-sm">Die Übersichten konnten nicht geladen werden.</p>;
  if (!id || !prefs)
    return (
      <p className="py-8 text-sm text-[var(--ink-soft)]" role="status">
        Übersicht wird vorbereitet …
      </p>
    );
  return (
    <>
      <DashboardScreen key={id} id={id} all={all} prefs={prefs} onSelect={onSelect} onNew={() => setNewOpen(true)} />
      <NewDashboardSheet open={newOpen} teams={list.data?.adminTeams ?? []} onClose={() => setNewOpen(false)} onCreate={create} />
    </>
  );
}

/** Boardübersicht: „Übersicht“ unter den Ansichten eines Boards (gleiche Bausteine, nur dieses Board) */
export function BoardOverview({ boardId, top }: { boardId: string; top?: ReactNode }) {
  const q = useQuery({
    queryKey: ['board-dashboard', boardId],
    queryFn: async () => {
      const d = await api.get<Dashboard>(`/api/boards/${boardId}/dashboard`);
      queryClient.setQueryData(['dashboard', d.id], d);
      return d;
    },
  });
  if (q.isError) return <p role="alert" className="p-4 text-sm">{(q.error as Error).message}</p>;
  if (!q.data) return <p className="p-4 text-sm text-[var(--ink-soft)]">Lädt …</p>;
  return (
    <div className="mx-auto w-full max-w-6xl px-3 pt-2 sm:px-4">
      {top}
      <DashboardScreen id={q.data.id} />
    </div>
  );
}

function DashboardScreen({
  id,
  all = [],
  prefs,
  onSelect,
  onNew,
}: {
  id: string;
  all?: DashboardListItem[];
  prefs?: DashboardPrefs;
  onSelect?: (id: string) => void;
  onNew?: () => void;
}) {
  const dash = useQuery({ queryKey: ['dashboard', id], queryFn: () => api.get<Dashboard>(`/api/dashboards/${id}`) });
  const { data: nav } = useNav();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [conflict, setConflict] = useState<Dashboard | null>(null);
  const [saving, setSaving] = useState(false);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [items, setItems] = useState<ItemsRequest | null>(null);
  const [announce, setAnnounce] = useState('');
  const [publish, setPublish] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [dashInfo, setDashInfo] = useState(false);
  const listQ = useDashboards();
  const phone = deviceClass() === 'phone';
  const pdf = usePdfAvailable();

  useEffect(() => realtime.watchCards(), []);

  const d = dash.data;
  const shown: Draft | null = draft ?? (d ? { title: d.title, filter: d.filter, widgets: d.widgets } : null);
  const recent = useMemo(() => recentBoardIds(), []);
  const data = useQuery({
    queryKey: ['dash-data', id, shown?.filter, shown?.widgets, recent],
    queryFn: () => api.post<DashboardDataResponse>('/api/dashboard-data', { filter: shown!.filter, widgets: shown!.widgets, recentBoardIds: recent }),
    enabled: !!shown,
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const results = useMemo(() => new Map((data.data?.results ?? []).map((r) => [r.id, r])), [data.data]);

  // Nach „Zurück“ aus einer Karte an derselben Stelle weiter (A08)
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !data.data) return;
    restored.current = true;
    try {
      const y = Number(sessionStorage.getItem(SCROLL_KEY));
      sessionStorage.removeItem(SCROLL_KEY);
      if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
    } catch {
      /* egal */
    }
  }, [data.data]);

  if (dash.isError) return <p role="alert" className="text-sm">{(dash.error as Error).message}</p>;
  if (!d || !shown) return <p className="py-8 text-sm text-[var(--ink-soft)]">Lädt …</p>;

  const editing = !!draft;
  const setWidgets = (widgets: Widget[]) => setDraft({ ...shown, widgets });
  const move = (from: number, to: number) => {
    if (to < 0 || to >= shown.widgets.length || from === to) return;
    setWidgets(arrayMove(shown.widgets, from, to));
    setAnnounce(`${widgetTitle(shown.widgets[from]!)} ist jetzt an Position ${to + 1} von ${shown.widgets.length}.`);
  };

  const save = async (expectedVersion: number) => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await api.put<Dashboard>(`/api/dashboards/${id}`, { ...draft, expectedVersion });
      queryClient.setQueryData(['dashboard', id], saved);
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      if (saved.boardId) queryClient.setQueryData(['board-dashboard', saved.boardId], saved);
      setDraft(null);
      setConflict(null);
      toast.info('Übersicht gespeichert.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict((e.data as { details?: { current?: Dashboard } })?.details?.current ?? null);
      else toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const ctx: DashCtx = { scope: d.scope, teamId: d.teamId };
  const shared = d.scope !== 'personal';
  const boardsText = (f: DashFilter) =>
    d.scope === 'board'
      ? 'Dieses Board'
      : f.boards.mode === 'all'
      ? 'Alle meine Boards'
      : f.boards.mode === 'team'
        ? 'Boards des Teams'
        : f.boards.mode === 'selected'
        ? `${f.boards.ids.length} ausgewählte ${f.boards.ids.length === 1 ? 'Board' : 'Boards'}`
        : `Bereich ${nav?.areas.find((a) => a.id === (f.boards as { areaId: string }).areaId)?.name ?? '(nicht verfügbar)'}`;
  // Filter der ganzen Übersicht einmal oben; Bausteine zeigen dauerhaft nur Abweichungen
  const globalChips = widgetFilterChips(shown.filter, { type: 'tasks', filter: {} }, undefined, { boardScope: true });
  const asOf = data.data ? new Date(data.data.generatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : null;
  const isStart = prefs?.startDashboardId === id;
  const personalCount = all.filter((x) => x.scope === 'personal').length;
  const copyToPersonal = async () => {
    const c = await api.post<Dashboard>('/api/dashboards', { copyOf: id });
    queryClient.setQueryData(['dashboard', c.id], c);
    await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    toast.info('Persönliche Kopie angelegt.');
    if (onSelect) onSelect(c.id);
    else navigate(`/?ort=uebersicht&d=${c.id}`);
  };
  const settingsWidget = shown.widgets.find((w) => w.id === settingsFor) ?? null;
  // Eigene Reihenfolge am Handy (nur Anzeige; die gemeinsame Konfiguration bleibt unverändert)
  const phoneOrder = listQ.data?.prefs.mobileOrder?.[id];
  const displayed = !editing && phone ? orderForPhone(shown.widgets, phoneOrder) : shown.widgets;

  return (
    <div className="flex flex-col gap-4 pb-10">
      {/* Kopf: Titel, Umschalten, Anpassen */}
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          {editing ? (
            <label className="flex flex-col gap-1 text-sm">
              <span className="sr-only">Name der Übersicht</span>
              <input className={cx(inputCls, 'text-lg font-semibold')} maxLength={80} value={shown.title} onChange={(e) => setDraft({ ...shown, title: e.target.value })} />
            </label>
          ) : !onSelect ? (
            <h2 className="px-1 text-xl font-bold break-words">{d.title}</h2>
          ) : (
            <DropdownMenu>
              {/* Überschrift für die Gliederung; sichtbar ist die Auswahl */}
              <h2 className="sr-only">{d.title}</h2>
              <DropdownTrigger asChild>
                <button type="button" className="inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-lg px-1 text-left text-xl font-bold hover:bg-black/5 dark:hover:bg-white/10" aria-label={`Übersicht wechseln, aktuell ${d.title}`}>
                  <span className="truncate">{d.title}</span>
                  <ChevronDown size={18} className="shrink-0" aria-hidden />
                </button>
              </DropdownTrigger>
              <DropdownContent align="start">
                {all.map((x, i) => (
                  <Fragment key={x.id}>
                    {x.scope === 'team' && all[i - 1]?.teamId !== x.teamId && <DropdownLabel>Team {x.teamName}</DropdownLabel>}
                    {x.scope === 'personal' && i === 0 && <DropdownLabel>Persönlich</DropdownLabel>}
                    <DropdownItem onSelect={() => onSelect(x.id)} icon={x.id === prefs?.startDashboardId ? <Home size={15} /> : <span className="inline-block w-[15px]" />}>
                      <span className={cx(x.id === id && 'font-semibold')}>{x.title}</span>
                    </DropdownItem>
                  </Fragment>
                ))}
                <DropdownSeparator />
                <DropdownItem icon={<Plus size={15} />} onSelect={() => onNew?.()}>
                  Neue Übersicht …
                </DropdownItem>
              </DropdownContent>
            </DropdownMenu>
          )}
        </div>
        {editing ? (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                setDraft(null);
                setConflict(null);
              }}
            >
              Abbrechen
            </Button>
            <Button variant="primary" disabled={saving || !shown.title.trim()} onClick={() => (shared ? setPublish(true) : void save(d.version))}>
              {shared ? 'Für alle speichern …' : 'Speichern'}
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            {d.canEdit && (
              <Button onClick={() => setDraft({ title: d.title, filter: d.filter, widgets: d.widgets })}>
                <Settings2 size={16} aria-hidden /> Anpassen
              </Button>
            )}
            <DropdownMenu>
              <DropdownTrigger asChild>
                <IconButton label="Menü der Übersicht">
                  <MoreHorizontal size={18} />
                </IconButton>
              </DropdownTrigger>
              <DropdownContent>
                <DropdownItem icon={<RefreshCw size={15} />} onSelect={() => void data.refetch()}>
                  Aktualisieren
                </DropdownItem>
                {phone && shown.widgets.length > 1 && (
                  <DropdownItem icon={<ArrowDown size={15} />} onSelect={() => setOrderOpen(true)}>
                    Reihenfolge am Handy …
                  </DropdownItem>
                )}
                {pdf && shown.widgets.length > 0 && (
                  <DropdownItem icon={<FileDown size={15} />} onSelect={() => setReportOpen(true)}>
                    Bericht exportieren …
                  </DropdownItem>
                )}
                {onNew && (
                  <DropdownItem icon={<Plus size={15} />} onSelect={onNew}>
                    Neue Übersicht …
                  </DropdownItem>
                )}
                <DropdownItem icon={<Copy size={15} />} onSelect={() => void copyToPersonal()}>
                  {shared ? 'Persönliche Kopie anlegen' : 'Kopie anlegen'}
                </DropdownItem>
                {d.scope !== 'board' && (
                <DropdownItem
                  icon={<Home size={15} />}
                  onSelect={async () => {
                    await api.put('/api/me/dashboard-prefs', { startDashboardId: isStart ? null : id });
                    await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
                    toast.info(isStart ? 'KaBa startet wieder mit der Boardliste.' : 'KaBa startet jetzt mit dieser Übersicht.');
                  }}
                >
                  {isStart ? 'Nicht mehr als Startseite' : 'Als Startseite verwenden'}
                </DropdownItem>
                )}
                {d.canEdit && d.scope !== 'board' && (d.scope === 'team' || personalCount > 1) && (
                  <>
                    <DropdownSeparator />
                    <DropdownItem
                      danger
                      icon={<Trash2 size={15} />}
                      onSelect={async () => {
                        if (!window.confirm(`Übersicht „${d.title}“ ${shared ? 'für alle Teammitglieder ' : ''}löschen? Es werden nur die Einstellungen gelöscht, keine Karten.`)) return;
                        await api.del(`/api/dashboards/${id}`);
                        await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
                        onSelect?.(all.find((x) => x.id !== id && x.scope === 'personal')!.id);
                      }}
                    >
                      Übersicht löschen
                    </DropdownItem>
                  </>
                )}
              </DropdownContent>
            </DropdownMenu>
          </div>
        )}
        {/* 0.86: Angaben unter Titel und Aktionen über die ganze Breite – am Handy kompakt in einer Zeile */}
        <p className="-mt-1.5 flex basis-full flex-wrap items-center gap-x-1.5 px-1 text-xs text-[var(--ink-soft)] sm:text-sm">
          <span>{boardsText(shown.filter)}</span>
          <span aria-hidden>·</span>
          <span>{d.scope === 'personal' ? 'Persönlich' : d.scope === 'team' ? `Team ${d.teamName ?? ''} · gemeinsam` : <>Gemeinsam<span className="hidden sm:inline"> für alle mit Zugriff</span></>}</span>
          {asOf && (
            <>
              <span aria-hidden>·</span>
              <span>
                Stand {asOf}
                {data.isFetching && ' (aktualisiert …)'}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="flex flex-col gap-1.5 px-1 text-xs text-[var(--ink-soft)]">
          <div className="flex flex-wrap items-center gap-1">
            {globalChips.length > 0 && (
              <ul className="flex flex-wrap items-center gap-1" aria-label="Aktive Filter der Übersicht">
                <li>Filter:</li>
                {globalChips.map((c) => (
                  <li key={c.label} className="rounded-full bg-black/5 px-2 py-0.5 dark:bg-white/10">
                    {c.label}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="inline-flex min-h-11 items-center gap-1 rounded-lg px-1.5 hover:bg-black/5 sm:min-h-8 dark:hover:bg-white/10"
              aria-expanded={dashInfo}
              aria-controls="dash-info"
              onClick={() => setDashInfo((x) => !x)}
            >
              <Info size={14} aria-hidden /> Was zählt hier?
            </button>
          </div>
          {dashInfo && (
            <p id="dash-info" className="rounded-lg bg-[var(--column)] p-2.5">
              Daten aus den Boards, auf die du Zugriff hast{shared ? ' – andere sehen mit derselben Übersicht unter Umständen andere Zahlen' : ''}. Erledigt heißt: Haken gesetzt. Jeder Baustein erklärt über <Info size={12} className="inline" aria-label="Info" /> genauer, was er zählt; farbig markierte Filter weichen von dieser Übersicht ab.
            </p>
          )}
        </div>

      {conflict && (
        <div role="alert" className="flex flex-col gap-2 rounded-xl border border-amber-400 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-100">
          <p>Diese Übersicht wurde inzwischen an anderer Stelle geändert (z. B. auf einem anderen Gerät).</p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                queryClient.setQueryData(['dashboard', id], conflict);
                setDraft(null);
                setConflict(null);
              }}
            >
              Andere Fassung übernehmen
            </Button>
            <Button variant="primary" onClick={() => void save(conflict.version)}>
              Meine Fassung speichern
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2 shadow-sm">
          <Button onClick={() => setAdding(true)}>
            <Plus size={16} aria-hidden /> Baustein hinzufügen
          </Button>
          <Button onClick={() => setFilterOpen(true)}>
            <SlidersHorizontal size={16} aria-hidden /> Filter der Übersicht
          </Button>
          <p className="basis-full px-1 text-xs text-[var(--ink-soft)]">Reihenfolge mit den Pfeilen, „Position“ oder am Griff ziehen. Gespeichert wird erst mit „Speichern“.</p>
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>

      {!shown.widgets.length ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--ink-soft)]">
          Noch keine Bausteine.{' '}
          {!editing && d.canEdit && (
            <button type="button" className="font-medium text-accent-fg underline" onClick={() => setDraft({ ...shown })}>
              Jetzt anpassen
            </button>
          )}
        </div>
      ) : (
        <Grid editing={editing} widgets={displayed} onReorder={setWidgets} onAnnounce={setAnnounce}>
          {(w, i, handle) => (
            <WidgetFrame
              key={w.id}
              w={w}
              index={i}
              count={shown.widgets.length}
              editing={editing}
              canEdit={d.canEdit}
              boardScope={d.scope === 'board'}
              handle={handle}
              dashFilter={shown.filter}
              result={results.get(w.id)}
              loading={data.isFetching}
              areaName={(aid) => nav?.areas.find((a) => a.id === aid)?.name}
              onRetry={() => void data.refetch()}
              onMove={(to) => move(i, to)}
              onSize={(size) => setWidgets(shown.widgets.map((x) => (x.id === w.id ? { ...x, size } : x)))}
              onRemove={() => {
                setWidgets(shown.widgets.filter((x) => x.id !== w.id));
                setAnnounce(`${widgetTitle(w)} entfernt.`);
              }}
              onSettings={() => {
                if (!editing) setDraft({ ...shown });
                setSettingsFor(w.id);
              }}
              openSet={(set) => setItems({ kind: 'set', title: widgetTitle(w), filter: effectiveFilter(shown.filter, w), set, sort: w.type === 'tasks' ? w.config.sort : 'due' })}
              openMetric={w.type === 'metric' ? (group) => setItems({ kind: 'metric', title: widgetTitle(w), filter: shown.filter, widget: w, group }) : undefined}
              openGroup={w.type === 'distribution' || w.type === 'workload' || w.type === 'punctuality' ? (key, label) => setItems({ kind: 'group', title: widgetTitle(w), filter: shown.filter, widget: w, key, label }) : undefined}
            />
          )}
        </Grid>
      )}

      <WidgetSettings
        ctx={ctx}
        open={!!settingsWidget}
        widget={settingsWidget}
        dashFilter={shown.filter}
        onClose={() => setSettingsFor(null)}
        onApply={(nw) => setWidgets(shown.widgets.map((x) => (x.id === nw.id ? nw : x)))}
      />
      <AddWidgetSheet
        ctx={ctx}
        open={adding}
        count={shown.widgets.length}
        onClose={() => setAdding(false)}
        onAdd={(w) => {
          setWidgets([...shown.widgets, w]);
          setAnnounce(`${widgetTitle(w)} am Ende hinzugefügt.`);
        }}
      />
      <DashFilterSheet ctx={ctx} open={filterOpen} filter={shown.filter} onClose={() => setFilterOpen(false)} onApply={(filter) => setDraft({ ...shown, filter })} />
      <ItemsSheet req={items} onClose={() => setItems(null)} />
      {orderOpen && <PhoneOrderSheet dashId={id} widgets={orderForPhone(shown.widgets, phoneOrder)} onClose={() => setOrderOpen(false)} />}
      {reportOpen && <DashboardReportDialog dash={d} open={reportOpen} onClose={() => setReportOpen(false)} />}
      <Sheet open={publish} onOpenChange={setPublish} title={d.scope === 'team' ? `Für das Team ${d.teamName ?? ''} speichern` : 'Boardübersicht für alle speichern'} side="center">
        <div className="flex flex-col gap-3 p-4 text-sm">
          <p>
            {d.scope === 'team' ? 'Alle Mitglieder des Teams' : 'Alle mit Zugriff auf dieses Board'} sehen danach diese gemeinsame Sicht: den Titel „{shown.title}“, die Bausteine mit ihren Titeln und die Filter.
          </p>
          <ul className="list-disc pl-5 text-[var(--ink-soft)]">
            {shown.widgets.map((w) => (
              <li key={w.id}>{widgetTitle(w)}</li>
            ))}
          </ul>
          <p>Zahlen und Karten sieht jede Person nur aus Boards, auf die sie selbst Zugriff hat. Ausgewählte Boards, die jemand nicht sieht, werden dieser Person nicht genannt – auch nicht als Anzahl.</p>
          <p className="text-[var(--ink-soft)]">Bitte keine vertraulichen Namen in Titel schreiben, die nicht alle sehen sollen.</p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setPublish(false)}>Zurück</Button>
            <Button
              variant="primary"
              disabled={saving}
              onClick={() => {
                setPublish(false);
                void save(d.version);
              }}
            >
              Für alle speichern
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}

/* ---------- Raster mit Anordnen (nur im Anpassungsmodus) ---------- */

type Handle = { attributes: Record<string, unknown>; listeners: Record<string, unknown> | undefined; setActivatorNodeRef: (el: HTMLElement | null) => void } | null;

function Grid({
  editing,
  widgets,
  onReorder,
  onAnnounce,
  children,
}: {
  editing: boolean;
  widgets: Widget[];
  onReorder: (w: Widget[]) => void;
  onAnnounce: (s: string) => void;
  children: (w: Widget, i: number, handle: Handle) => ReactNode;
}) {
  // Touch erst nach Halten am Griff – normales Scrollen löst nie ein Ziehen aus (G03)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const cls = 'grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-12';
  if (!editing)
    return (
      <div className={cls}>
        {widgets.map((w, i) => (
          <div key={w.id} className={cx('min-w-0', SPAN[w.size])}>
            {children(w, i, null)}
          </div>
        ))}
      </div>
    );
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = widgets.findIndex((w) => w.id === e.active.id);
    const to = widgets.findIndex((w) => w.id === e.over!.id);
    onReorder(arrayMove(widgets, from, to));
    onAnnounce(`${widgetTitle(widgets[from]!)} ist jetzt an Position ${to + 1}.`);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
        <div className={cls}>
          {widgets.map((w, i) => (
            <SortableCell key={w.id} w={w}>
              {(handle) => children(w, i, handle)}
            </SortableCell>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableCell({ w, children }: { w: Widget; children: (h: Handle) => ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: w.id });
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} className={cx('min-w-0', SPAN[w.size], isDragging && 'opacity-80')}>
      {children({ attributes: attributes as unknown as Record<string, unknown>, listeners, setActivatorNodeRef })}
    </div>
  );
}

/* ---------- Baustein ---------- */

function WidgetFrame({
  w,
  index,
  count,
  editing,
  canEdit,
  boardScope,
  handle,
  dashFilter,
  result,
  loading,
  areaName,
  onRetry,
  onMove,
  onSize,
  onRemove,
  onSettings,
  openSet,
  openMetric,
  openGroup,
}: {
  w: Widget;
  index: number;
  count: number;
  editing: boolean;
  canEdit: boolean;
  boardScope: boolean;
  handle: Handle;
  dashFilter: DashFilter;
  result: WidgetResult | undefined;
  loading: boolean;
  areaName: (id: string) => string | undefined;
  onRetry: () => void;
  onMove: (to: number) => void;
  onSize: (s: WidgetSize) => void;
  onRemove: () => void;
  onSettings: () => void;
  openSet: (set: DashSet, total: number) => void;
  openMetric?: (group: string | null) => void;
  openGroup?: (key: string, label: string) => void;
}) {
  const title = widgetTitle(w);
  const chips = widgetFilterChips(dashFilter, w, areaName, { boardScope });
  const ownChips = chips.filter((c) => c.own);
  const [info, setInfo] = useState(false);
  const notes = widgetNotes(w, result?.status === 'ok' ? result.data : undefined);
  const hid = `wt-${w.id}`;
  return (
    <section aria-labelledby={hid} className={cx('flex flex-col rounded-2xl border bg-[var(--panel)] p-4', editing ? 'border-dashed border-accent/60' : 'border-[var(--line)]')}>
      <header className="flex items-start gap-2">
        {editing && handle && (
          <button
            type="button"
            ref={handle.setActivatorNodeRef}
            {...handle.attributes}
            {...handle.listeners}
            aria-label={`${title} verschieben (Griff)`}
            className="-ml-1 inline-flex size-11 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 sm:size-9 dark:hover:bg-white/10"
          >
            <GripVertical size={18} aria-hidden />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h3 id={hid} className="flex min-h-11 items-center text-base font-semibold break-words sm:min-h-9">
            {title}
          </h3>
          {/* Dauerhaft nur abweichende Filter; alles Weitere über „Info“ */}
          {ownChips.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-1" aria-label="Abweichende Filter">
              {ownChips.map((c) => (
                <li key={c.label} className="rounded-full bg-accent-soft px-2 py-0.5 text-xs text-[var(--ink)]">
                  {c.label}
                  <span className="sr-only"> (abweichend)</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <IconButton label={`Info: ${title}`} aria-expanded={info} aria-controls={`${hid}-info`} onClick={() => setInfo((x) => !x)} className={cx(info && 'bg-accent-soft')}>
          <Info size={17} />
        </IconButton>
        {!editing && (
          <DropdownMenu>
            <DropdownTrigger asChild>
              <IconButton label={`Menü: ${title}`}>
                <MoreHorizontal size={17} />
              </IconButton>
            </DropdownTrigger>
            <DropdownContent>
              <DropdownItem icon={<RefreshCw size={15} />} onSelect={onRetry}>
                Aktualisieren
              </DropdownItem>
              {canEdit && (
                <DropdownItem icon={<Settings2 size={15} />} onSelect={onSettings}>
                  Einstellungen …
                </DropdownItem>
              )}
            </DropdownContent>
          </DropdownMenu>
        )}
      </header>

      {info && (
        <div id={`${hid}-info`} className="mt-2 flex flex-col gap-1.5 rounded-lg bg-[var(--column)] p-2.5 text-xs text-[var(--ink-soft)]">
          <p>{WIDGET_INFO[w.type].text}.</p>
          {notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
          {chips.length > 0 ? (
            <div>
              <span>Wirksame Filter:</span>
              <ul className="mt-1 flex flex-wrap gap-1" aria-label="Wirksame Filter">
                {chips.map((c) => (
                  <li key={c.label} className={cx('rounded-full px-2 py-0.5', c.own ? 'bg-accent-soft text-[var(--ink)]' : 'bg-black/5 dark:bg-white/10')}>
                    {c.label}
                    {c.own && <span className="sr-only"> (abweichend)</span>}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p>Keine Filter.</p>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-y border-[var(--line)] py-2">
          <IconButton label={`${title} nach oben`} disabled={index === 0} onClick={() => onMove(index - 1)}>
            <ArrowUp size={17} />
          </IconButton>
          <IconButton label={`${title} nach unten`} disabled={index === count - 1} onClick={() => onMove(index + 1)}>
            <ArrowDown size={17} />
          </IconButton>
          <label className="flex items-center gap-1 text-xs">
            <span>Position</span>
            <select className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-sm sm:min-h-9" value={index} onChange={(e) => onMove(Number(e.target.value))} aria-label={`Position von ${title}`}>
              {Array.from({ length: count }, (_, i) => (
                <option key={i} value={i}>
                  {i + 1}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-xs">
            <span>Breite</span>
            <select className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-sm sm:min-h-9" value={w.size} onChange={(e) => onSize(Number(e.target.value) as WidgetSize)} aria-label={`Breite von ${title}`}>
              {([4, 6, 12] as WidgetSize[]).map((s) => (
                <option key={s} value={s}>
                  {SIZE_TEXT[s]}
                </option>
              ))}
            </select>
          </label>
          <span className="flex-1" />
          <IconButton label={`Einstellungen: ${title}`} onClick={onSettings}>
            <Settings2 size={17} />
          </IconButton>
          <IconButton label={`${title} entfernen`} onClick={onRemove}>
            <Trash2 size={17} />
          </IconButton>
        </div>
      )}

      <div className="mt-3 min-w-0 flex-1">
        {!result ? (
          loading || !editing ? (
            <div aria-busy="true" className="flex flex-col gap-2" role="status">
              <span className="sr-only">Lädt …</span>
              <div className="h-4 w-2/3 animate-pulse rounded bg-black/5 dark:bg-white/10" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-black/5 dark:bg-white/10" />
            </div>
          ) : (
            <p className="text-sm text-[var(--ink-soft)]">Wird nach dem Anpassen geladen …</p>
          )
        ) : result.status === 'error' ? (
          <div role="alert" className="flex flex-col items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-900 dark:bg-rose-950 dark:text-rose-100">
            <span>{result.message}</span>
            <Button size="sm" onClick={onRetry}>
              Erneut versuchen
            </Button>
          </div>
        ) : (
          <WidgetBody w={w} data={result.data} openSet={openSet} openMetric={openMetric} openGroup={openGroup} filter={dashFilter} />
        )}
      </div>
    </section>
  );
}

/** Eigene Reihenfolge am Handy – ohne Ziehen, nur für dich, auch bei gemeinsamen Übersichten */
function PhoneOrderSheet({ dashId, widgets, onClose }: { dashId: string; widgets: Widget[]; onClose: () => void }) {
  const [list, setList] = useState(widgets);
  const [busy, setBusy] = useState(false);
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    setList(arrayMove(list, i, j));
  };
  const save = async (ids: string[]) => {
    setBusy(true);
    try {
      const p = await api.put<DashboardPrefs>('/api/me/dashboard-prefs', { mobileOrder: { dashboardId: dashId, widgetIds: ids } });
      queryClient.setQueryData<ListRes>(['dashboards'], (old) => (old ? { ...old, prefs: p } : old));
      toast.info(ids.length ? 'Reihenfolge am Handy gespeichert.' : 'Am Handy gilt wieder die gemeinsame Reihenfolge.');
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title="Reihenfolge am Handy" side="center">
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Gilt nur für dich und nur auf dem Handy. Am Desktop bleibt die Anordnung der Übersicht.</p>
        <ol className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
          {list.map((w, i) => (
            <li key={w.id} className="flex items-center gap-1 px-2 py-1">
              <span className="min-w-0 flex-1 text-sm break-words">
                {i + 1}. {widgetTitle(w)}
              </span>
              <IconButton label={`${widgetTitle(w)} nach oben`} disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUp size={17} />
              </IconButton>
              <IconButton label={`${widgetTitle(w)} nach unten`} disabled={i === list.length - 1} onClick={() => move(i, 1)}>
                <ArrowDown size={17} />
              </IconButton>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => void save([])}>
            Wie am Desktop
          </Button>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={busy} onClick={() => void save(list.map((w) => w.id))}>
            Speichern
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
